import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import gsap from "gsap"

interface ChipCardOverlayProps {
  active: boolean
  large?: boolean
}

export function ChipCardOverlay({ active, large = false }: ChipCardOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const scanRef = useRef<HTMLDivElement>(null)
  const borderPathRef = useRef<SVGPathElement>(null)

  useEffect(() => {
    if (!active || !overlayRef.current) return

    const ctx = gsap.context(() => {
      if (frameRef.current) {
        gsap.fromTo(frameRef.current, { opacity: 0 }, { opacity: 1, duration: 0.45, ease: "power2.out" })
      }

      if (borderPathRef.current) {
        const length = borderPathRef.current.getTotalLength()
        const segment = Math.max(48, length * 0.12)

        gsap.set(borderPathRef.current, {
          strokeDasharray: `${segment} ${length}`,
          strokeDashoffset: 0,
        })

        gsap.to(borderPathRef.current, {
          strokeDashoffset: -length,
          duration: 2.4,
          repeat: -1,
          ease: "none",
        })
      }

      const traces = overlayRef.current?.querySelectorAll(".chip-trace")
      if (traces?.length) {
        gsap.fromTo(
          traces,
          { strokeDashoffset: 180, opacity: 0 },
          { strokeDashoffset: 0, opacity: 0.55, duration: 0.7, stagger: 0.06, ease: "power2.out" },
        )
      }

      if (scanRef.current) {
        gsap.fromTo(scanRef.current, { y: "-100%" }, { y: "200%", duration: 2.8, repeat: -1, ease: "none", delay: 0.4 })
      }
    }, overlayRef)

    return () => ctx.revert()
  }, [active])

  return (
    <div
      ref={overlayRef}
      className={cn(
        "pointer-events-none absolute inset-0 transition-opacity duration-500",
        active ? "opacity-100" : "opacity-0",
      )}
      aria-hidden="true"
    >
      {/* Traveling edge stroke around card */}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path
          ref={borderPathRef}
          d="M 0.5 0.5 H 99.5 V 99.5 H 0.5 V 0.5"
          fill="none"
          stroke="oklch(0.7 0.2 45)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Inner frame */}
      <div
        ref={frameRef}
        className="absolute inset-[10px] border border-border/50 opacity-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, oklch(0.93 0 0 / 0.04) 1px, transparent 1px), linear-gradient(to bottom, oklch(0.93 0 0 / 0.04) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Minimal circuit traces — two step lines */}
      <svg
        className="absolute inset-[10px] h-[calc(100%-20px)] w-[calc(100%-20px)]"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          className="chip-trace"
          d={large ? "M 0 16 H 36 V 11 H 88" : "M 0 22 H 38 V 16 H 72"}
          fill="none"
          stroke="oklch(0.93 0 0)"
          strokeWidth={1}
          strokeDasharray={180}
          vectorEffect="non-scaling-stroke"
        />
        <path
          className="chip-trace"
          d={large ? "M 0 30 H 50 V 36 H 92" : "M 0 42 H 48 V 48 H 90"}
          fill="none"
          stroke="oklch(0.93 0 0)"
          strokeWidth={1}
          strokeDasharray={180}
          vectorEffect="non-scaling-stroke"
        />
        {!large && (
          <rect
            className="chip-trace"
            x="44"
            y="44"
            width="12"
            height="12"
            fill="none"
            stroke="oklch(0.93 0 0 / 0.45)"
            strokeWidth={1}
            strokeDasharray={180}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      {/* Single scanline */}
      <div className="absolute inset-[10px] overflow-hidden opacity-40">
        <div
          ref={scanRef}
          className="absolute left-0 h-px w-full bg-gradient-to-r from-transparent via-foreground/50 to-transparent"
        />
      </div>
    </div>
  )
}
